// Cave84 — Appellations hooks (Phase 1 Week 3)
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Appellation } from '@cave84/shared-types';
import {
  createAppellation, getAppellation, listAppellations,
  type AppellationCreateInput
} from '@/services/appellations';
import { qk } from '@/services/firestore';
import { useAuthStore } from '@/stores/auth.store';
import { foldSearch } from '@/utils/searchText';

function assertOwner() {
  const role = useAuthStore.getState().userDoc?.role;
  if (role !== 'owner') throw new Error('Cave84: owner role required.');
}

export function useAppellations() {
  return useQuery({ queryKey: qk.appellations.all, queryFn: listAppellations });
}

export function useAppellation(id: string | undefined) {
  return useQuery({
    queryKey: id ? qk.appellations.byId(id) : qk.appellations.byId('__noop__'),
    queryFn: () => (id ? getAppellation(id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

export function useAppellationSearch(term: string) {
  const { data: appellations } = useAppellations();
  const lower = foldSearch(term.trim());
  if (!lower || !appellations) return appellations ?? [];
  return appellations.filter(a =>
    foldSearch(a.name).includes(lower) ||
    foldSearch(a.hierarchy_path).includes(lower)
  );
}

export function useCreateAppellation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AppellationCreateInput) => { assertOwner(); return createAppellation(input); },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.appellations.all }); },
  });
}

/** Convenience: build a Map<id, Appellation> from the cached list. */
export function useAppellationsMap(): Map<string, Appellation> {
  const { data } = useAppellations();
  return new Map((data ?? []).map(a => [a.id, a]));
}
